import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const touchInputQuery = "(any-pointer: coarse), (hover: none), (pointer: none)";

function supportsLavaLamp() {
  return !window.matchMedia(touchInputQuery).matches;
}

type LavaLampIndicatorOptions = {
  activeSelector?: string;
  itemSelector?: string;
};

export default function useLavaLampIndicator<T extends HTMLElement>({
  activeSelector = ".active",
  itemSelector = "a, button",
}: LavaLampIndicatorOptions = {}) {
  const containerRef = useRef<T>(null);
  const focusedItemRef = useRef<HTMLElement | null>(null);
  const hoveredItemRef = useRef<HTMLElement | null>(null);

  const getItem = useCallback(
    (target: EventTarget | null) => {
      const container = containerRef.current;
      if (!(target instanceof Element) || !container) return null;

      const item = target.closest<HTMLElement>(itemSelector);
      return item && container.contains(item) ? item : null;
    },
    [itemSelector]
  );

  const moveIndicator = useCallback(
    (item?: HTMLElement | null) => {
      const container = containerRef.current;
      if (!container) return;

      container
        .querySelectorAll<HTMLElement>("[data-lava-target]")
        .forEach((previousTarget) => {
          delete previousTarget.dataset.lavaTarget;
        });

      if (!supportsLavaLamp()) {
        delete container.dataset.lavaReady;
        delete container.dataset.lavaVisible;
        delete container.dataset.lavaPressed;
        delete container.dataset.lavaInstant;
        return;
      }

      const target =
        item ?? container.querySelector<HTMLElement>(activeSelector);

      if (!target) {
        container.dataset.lavaVisible = "false";
        container.dataset.lavaReady = "true";
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const shouldEnterInPlace = container.dataset.lavaVisible !== "true";

      if (shouldEnterInPlace) {
        container.dataset.lavaInstant = "true";
      }

      container.style.setProperty(
        "--lava-x",
        `${targetRect.left - containerRect.left + container.scrollLeft}px`
      );
      container.style.setProperty("--lava-width", `${targetRect.width}px`);
      target.dataset.lavaTarget = "true";
      container.dataset.lavaVisible = "true";
      container.dataset.lavaReady = "true";

      if (shouldEnterInPlace) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (containerRef.current === container) {
              delete container.dataset.lavaInstant;
            }
          });
        });
      }
    },
    [activeSelector]
  );

  const restorePreferredItem = useCallback(() => {
    moveIndicator(focusedItemRef.current ?? hoveredItemRef.current);
  }, [moveIndicator]);

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    moveIndicator();
    const inputMedia = window.matchMedia(touchInputQuery);
    const handleInputChange = () => {
      focusedItemRef.current = null;
      hoveredItemRef.current = null;
      moveIndicator();
    };
    inputMedia.addEventListener("change", handleInputChange);

    const resizeObserver = new ResizeObserver(() => restorePreferredItem());
    resizeObserver.observe(container);
    container
      .querySelectorAll<HTMLElement>(itemSelector)
      .forEach((item) => resizeObserver.observe(item));

    const mutationObserver = new MutationObserver(() => restorePreferredItem());
    mutationObserver.observe(container, {
      attributeFilter: ["class", "data-active", "aria-checked"],
      attributes: true,
      subtree: true,
    });

    const handleScroll = () => restorePreferredItem();
    container.addEventListener("scroll", handleScroll, { passive: true });

    let isMounted = true;
    document.fonts?.ready.then(() => {
      if (isMounted) restorePreferredItem();
    });

    return () => {
      isMounted = false;
      inputMedia.removeEventListener("change", handleInputChange);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      container.removeEventListener("scroll", handleScroll);
    };
  }, [itemSelector, moveIndicator, restorePreferredItem]);

  function handlePointerOver(event: React.PointerEvent<T>) {
    if (!supportsLavaLamp() || event.pointerType === "touch") return;
    const item = getItem(event.target);
    if (!item || item === hoveredItemRef.current) return;

    hoveredItemRef.current = item;
    moveIndicator(item);
  }

  function handlePointerLeave() {
    const container = containerRef.current;
    hoveredItemRef.current = null;
    if (container) container.dataset.lavaPressed = "false";
    restorePreferredItem();
  }

  function handlePointerDown(event: React.PointerEvent<T>) {
    if (!supportsLavaLamp() || event.pointerType === "touch") return;
    const container = containerRef.current;
    const item = getItem(event.target);
    if (!container || !item) return;

    moveIndicator(item);
    container.dataset.lavaPressed = "true";
  }

  function handlePointerEnd() {
    const container = containerRef.current;
    if (container) container.dataset.lavaPressed = "false";
  }

  function handleFocus(event: React.FocusEvent<T>) {
    const item = getItem(event.target);
    if (!item) return;

    focusedItemRef.current = item;
    moveIndicator(item);
  }

  function handleBlur(event: React.FocusEvent<T>) {
    const nextItem = getItem(event.relatedTarget);
    focusedItemRef.current = nextItem;
    restorePreferredItem();
  }

  function handleKeyDown(event: React.KeyboardEvent<T>) {
    if (!supportsLavaLamp()) return;
    if (event.key !== "Enter") return;

    const container = containerRef.current;
    if (container) container.dataset.lavaPressed = "true";
  }

  return {
    containerRef,
    lavaLampHandlers: {
      onBlurCapture: handleBlur,
      onFocusCapture: handleFocus,
      onKeyDownCapture: handleKeyDown,
      onKeyUpCapture: handlePointerEnd,
      onPointerCancel: handlePointerEnd,
      onPointerDown: handlePointerDown,
      onPointerLeave: handlePointerLeave,
      onPointerOver: handlePointerOver,
      onPointerUp: handlePointerEnd,
    },
  };
}
